/**
 * URL-persisted ticker filters for CM Bet Ticker.
 */

import {
  CURRENT_TICKER_STATUSES,
  DEFAULT_TICKER_FILTERS,
  PREVIOUS_RANGE_MAX_DAYS,
  PREVIOUS_TICKER_STATUSES,
  TICKER_CURRENCIES,
  TICKER_PAGE_SIZES,
  type BetSide,
  type TickerFilters,
  type TickerPageSize,
} from './domain'
import { sanitizeMarketType } from './marketType'

const SEARCH_MAX = 80
const MEMBER_MAX = 40
const MEMBER_LEN = 64
const STAKE_MAX = 10_000_000
const SIDES = new Set<BetSide | 'all'>(['all', 'BACK', 'LAY'])
const PAGE_SIZES = new Set<number>(TICKER_PAGE_SIZES)
const ALL_STATUS = new Set<string>([...CURRENT_TICKER_STATUSES, ...PREVIOUS_TICKER_STATUSES])

function stripControls(raw: string): string {
  let out = ''
  for (const ch of raw) {
    const code = ch.charCodeAt(0)
    if (code < 32 || code === 127) continue
    out += ch
  }
  return out
}

function sanitizeSearch(raw: string): string {
  return stripControls(raw).trim().slice(0, SEARCH_MAX)
}

function sanitizeToken(raw: string, max = 80): string {
  return stripControls(raw).trim().slice(0, max)
}

function sanitizeMember(raw: string): string | null {
  const value = sanitizeToken(raw, MEMBER_LEN)
  if (!value) return null
  if (!/^[A-Za-z0-9._:@-]+$/.test(value)) return null
  return value
}

function sanitizeMembers(raw: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const item of raw) {
    const id = sanitizeMember(item)
    if (!id) continue
    const key = id.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(id)
    if (out.length >= MEMBER_MAX) break
  }
  return out
}

const CURRENCIES = new Set<string>(TICKER_CURRENCIES)

function sanitizeCurrency(raw: string): string {
  const value = sanitizeToken(raw, 16).toUpperCase()
  if (!value || value === 'ALL') return 'all'
  if (value === 'POINT' || value === 'POINTS') return 'POINTS'
  if (value === 'POINT@1' || value === 'POINT_AT_1' || value === 'POINT1') {
    return 'POINT@1'
  }
  return CURRENCIES.has(value) ? value : 'all'
}

function sanitizeStatus(raw: string): string {
  const value = stripControls(raw).trim().toUpperCase()
  if (!value || value === 'ALL') return 'all'
  return ALL_STATUS.has(value) ? value : 'all'
}

function parseStake(raw: string | null): number | null {
  if (raw == null || raw === '') return null
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 0) return null
  return Math.min(STAKE_MAX, Math.round(n * 100) / 100)
}

function isIsoDay(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`))
}

/** Local calendar YYYY-MM-DD (never UTC — IST midnight stays today). */
export function tickerLocalIsoDay(d: Date = new Date()): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export const TICKER_FUTURE_DATE_MESSAGE = 'Dates cannot be in the future'
export const TICKER_TO_BEFORE_FROM_MESSAGE = 'To Date cannot be earlier than From Date'

/**
 * Same window as CM-API ticker + request body (`from` 00:00Z → `to` 23:59:59.999Z).
 * Midnight-only gaps undercount: Oct 4→Nov 4 is 31 midnights but ~32 duration days.
 */
export function tickerRangeWithinMax(
  from: string,
  to: string,
  maxDays: number,
): boolean {
  if (!from || !to) return true
  const days =
    (Date.parse(`${to}T23:59:59.999Z`) - Date.parse(`${from}T00:00:00.000Z`)) /
    86_400_000
  return Number.isFinite(days) && days <= maxDays
}

/**
 * Inline From/To guards — empty ends allowed (page shows range-required empty).
 * Returns toast copy, or null when the change is OK.
 */
export function tickerDateRangeError(
  from: string,
  to: string,
  maxDays: number,
  now: Date = new Date(),
): string | null {
  const today = tickerLocalIsoDay(now)
  if (from) {
    if (!isIsoDay(from)) return 'Enter a valid From Date'
    if (from > today) return TICKER_FUTURE_DATE_MESSAGE
  }
  if (to) {
    if (!isIsoDay(to)) return 'Enter a valid To Date'
    if (to > today) return TICKER_FUTURE_DATE_MESSAGE
  }
  if (from && to && from > to) return TICKER_TO_BEFORE_FROM_MESSAGE
  if (from && to && !tickerRangeWithinMax(from, to, maxDays)) {
    return `Choose a range of ${maxDays} days or less.`
  }
  return null
}

function clampDateRange(from: string, to: string, now: Date = new Date()): { from: string; to: string } {
  const today = tickerLocalIsoDay(now)
  let start = from && isIsoDay(from) ? from : ''
  let end = to && isIsoDay(to) ? to : ''
  if (start && start > today) start = today
  if (end && end > today) end = today
  if (start && end && start > end) {
    const swap = start
    start = end
    end = swap
  }
  if (start && end && !tickerRangeWithinMax(start, end, PREVIOUS_RANGE_MAX_DAYS)) {
    // Duration uses end-of-day, so max midnight gap is maxDays - 1.
    const capped = new Date(
      Date.parse(`${start}T00:00:00`) +
        (PREVIOUS_RANGE_MAX_DAYS - 1) * 86_400_000,
    )
    end = tickerLocalIsoDay(capped)
    if (end > today) end = today
  }
  return { from: start, to: end }
}

export function parseTickerFilters(params: URLSearchParams): TickerFilters {
  const sideRaw = (params.get('side') ?? 'all').toUpperCase()
  const sizeRaw = Number(params.get('size') ?? DEFAULT_TICKER_FILTERS.pageSize)
  const pageRaw = Number(params.get('page') ?? 1)
  const { from, to } = clampDateRange(params.get('from') ?? '', params.get('to') ?? '')

  // Do not swap min/max here — while typing Stake to (e.g. 200 after From 100)
  // the first digit is briefly smaller and a swap rewrites both fields.
  const stakeMin = parseStake(params.get('smin'))
  const stakeMax = parseStake(params.get('smax'))

  return {
    tmId: sanitizeToken(params.get('tm') ?? '', 128),
    members: sanitizeMembers(params.getAll('member')),
    search: sanitizeSearch(params.get('q') ?? ''),
    sport: sanitizeToken(params.get('sport') ?? 'all', 64) || 'all',
    event: sanitizeToken(params.get('event') ?? '', 256),
    market: sanitizeToken(params.get('market') ?? '', 256),
    marketType: sanitizeMarketType(params.get('mtype') ?? 'all'),
    side: SIDES.has(sideRaw as BetSide | 'all') ? (sideRaw as BetSide | 'all') : 'all',
    status: sanitizeStatus(params.get('status') ?? 'all'),
    currency: sanitizeCurrency(params.get('ccy') ?? 'all'),
    flagged:
      params.get('flagged') === 'final'
        ? 'final'
        : params.get('flagged') === '1'
          ? 'flagged'
          : 'all',
    from,
    to,
    stakeMin,
    stakeMax,
    page: Number.isFinite(pageRaw) && pageRaw > 0 ? Math.floor(pageRaw) : 1,
    pageSize: PAGE_SIZES.has(sizeRaw) ? (sizeRaw as TickerPageSize) : DEFAULT_TICKER_FILTERS.pageSize,
  }
}

export function serializeTickerFilters(filters: TickerFilters): URLSearchParams {
  const params = new URLSearchParams()
  const d = DEFAULT_TICKER_FILTERS
  if (filters.tmId) params.set('tm', filters.tmId)
  for (const id of filters.members) params.append('member', id)
  if (filters.search) params.set('q', filters.search)
  if (filters.sport !== d.sport) params.set('sport', filters.sport)
  if (filters.event) params.set('event', filters.event)
  if (filters.market) params.set('market', filters.market)
  if (filters.marketType !== d.marketType) params.set('mtype', filters.marketType)
  if (filters.side !== d.side) params.set('side', filters.side)
  if (filters.status !== d.status) params.set('status', filters.status)
  if (filters.currency !== d.currency) params.set('ccy', filters.currency)
  if (filters.flagged === 'flagged') params.set('flagged', '1')
  if (filters.flagged === 'final') params.set('flagged', 'final')
  if (filters.from) params.set('from', filters.from)
  if (filters.to) params.set('to', filters.to)
  if (filters.stakeMin != null) params.set('smin', String(filters.stakeMin))
  if (filters.stakeMax != null) params.set('smax', String(filters.stakeMax))
  if (filters.page !== d.page) params.set('page', String(filters.page))
  if (filters.pageSize !== d.pageSize) params.set('size', String(filters.pageSize))
  return params
}

export function patchTickerFilters(
  current: TickerFilters,
  patch: Partial<TickerFilters>,
): TickerFilters {
  const next = { ...current, ...patch }
  next.members = sanitizeMembers(next.members)
  next.search = sanitizeSearch(next.search)
  const pageTouched = Object.prototype.hasOwnProperty.call(patch, 'page')
  const sizeOnly =
    Object.keys(patch).length === 1 && Object.prototype.hasOwnProperty.call(patch, 'pageSize')
  if (!pageTouched && !sizeOnly) next.page = 1
  const { from, to } = clampDateRange(next.from, next.to)
  next.from = from
  next.to = to
  return next
}

/**
 * When both ends are set and inverted, return From ≤ To.
 * Call on blur (not on every keystroke) so typing "600" after From 800 still works.
 */
export function orderStakeBounds(
  stakeMin: number | null,
  stakeMax: number | null,
): { stakeMin: number | null; stakeMax: number | null } {
  if (stakeMin != null && stakeMax != null && stakeMin > stakeMax) {
    return { stakeMin: stakeMax, stakeMax: stakeMin }
  }
  return { stakeMin, stakeMax }
}

export function previousRangeMissing(filters: TickerFilters): boolean {
  return !filters.from || !filters.to
}

/** True when any non-default filter is active (dates ignored — Previous always has them). */
export function filtersAreActive(filters: TickerFilters): boolean {
  const d = DEFAULT_TICKER_FILTERS
  return (
    Boolean(filters.tmId) ||
    filters.members.length > 0 ||
    filters.search !== d.search ||
    filters.sport !== d.sport ||
    Boolean(filters.event) ||
    Boolean(filters.market) ||
    filters.marketType !== d.marketType ||
    filters.side !== d.side ||
    filters.status !== d.status ||
    filters.currency !== d.currency ||
    filters.flagged !== d.flagged ||
    filters.stakeMin != null ||
    filters.stakeMax != null
  )
}

/** Count of secondary filters living under the More popover. */
export function countMoreFilters(
  filters: TickerFilters,
  opts?: { includeFlagged?: boolean },
): number {
  const d = DEFAULT_TICKER_FILTERS
  let n = 0
  if (filters.sport !== d.sport) n++
  if (filters.event) n++
  if (filters.marketType !== d.marketType) n++
  if (filters.market) n++
  if (filters.currency !== d.currency) n++
  if (filters.stakeMin != null || filters.stakeMax != null) n++
  if (opts?.includeFlagged && filters.flagged !== d.flagged) n++
  return n
}
